import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_LINE_KEY_TEMPLATE } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputText from "@/design/forms/InputText";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viArrowRight, viBlf, viCopy, viSpinnerThird } from "@sippy-platform/valkyrie";

import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import LineKeyEditor from "./lineKeyEditor/LineKeyEditor";
import lineKeyTemplateReducer from "./lineKeyTemplateReducer";

function LineKeyTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const deviceStore = useSelector((state) => state.backstage.devices);

  const [specs, setSpecs] = useState(null);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getDevices(true));
  }, []);

  const {
    value: lineKeyTemplate,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_LINE_KEY_TEMPLATE,
    reducer: lineKeyTemplateReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 80 characters." }),
    }),
    onSubmit: () => Api.storeLineKeyTemplate(lineKeyTemplate),
    onSuccess: (response) => {
      if (!lineKeyTemplate.id) {
        dispatch({ type: "id", value: response.data.lineKeyTemplate.id });
        navigate(`../${response.data.lineKeyTemplate.id}`);
      }

      Promise.resolve(Api.getLineKeyTemplates());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === lineKeyTemplate.id || !id) {
      return;
    }

    setSaveState(SaveState.UNCHANGED);

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_LINE_KEY_TEMPLATE });

      Promise.resolve(Api.getDeviceById(NEW_LINE_KEY_TEMPLATE.preferredDevice)).then((response) =>
        setSpecs(response.data.device),
      );

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getLineKeyTemplate({ id: Number(id) }))
      .then((response) => {
        if (!ignore) {
          dispatch({ type: "set", value: response.data.lineKeyTemplate });

          Promise.resolve(
            Api.getDeviceById(
              response.data.lineKeyTemplate.preferredDevice
                ? response.data.lineKeyTemplate.preferredDevice
                : { id: 30 },
            ),
          ).then((response) => setSpecs(response.data.device));
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dispatch, id, lineKeyTemplate.id, setSaveState]);

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "preferredDevice": {
        dispatch({ type: property, value, list: deviceStore.list });

        Promise.resolve(Api.getDeviceById({ id: value })).then((response) => setSpecs(response.data.device));
        break;
      }
      default:
        dispatch({ type: property, value });
    }

    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getLineKeyTemplates()).then(() => navigate(".."));
  }

  // Copy
  const [pendingDuplicate, setPendingDuplicate] = useState(false);
  const [duplicateId, setDuplicatedId] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  function duplicateLineKeyTemplate() {
    setPendingDuplicate(true);

    Promise.resolve(Api.copyLineKeyTemplate(lineKeyTemplate))
      .then((response) => {
        setDuplicatedId(response.data.lineKeyTemplate.id);
        setDuplicateDialogOpen(true);

        Promise.resolve(Api.getLineKeyTemplates());
      })
      .finally(() => {
        setPendingDuplicate(false);
      });
  }

  return (
    <Container>
      <title>{`${lineKeyTemplate.name || "Line key template"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} /> <Avatar text={lineKeyTemplate.name} icon={viBlf} />
              </IconButton>
            ) : (
              <Avatar text={lineKeyTemplate.name} icon={viBlf} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={lineKeyTemplate.id || "nk"}
                isActionable={!!lineKeyTemplate?.id}
                onDeleteCheck={() => Api.deleteLineKeyTemplate(lineKeyTemplate, false)}
                onDelete={() => Api.deleteLineKeyTemplate(lineKeyTemplate, true)}
                name={`${lineKeyTemplate.name}`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={lineKeyTemplate.name || "Unnamed line key template"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required
              id="name"
              label="Name"
              onChange={formHandler}
              value={lineKeyTemplate.name}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputAutocomplete
              id="preferredDevice"
              label="Preview device layout"
              onChange={formHandler}
              value={lineKeyTemplate.preferredDevice?.id ?? 30}
              selectValue="id"
              selects={deviceStore.list}
              getOptionLabel={(option) => `${option.brand} ${option.type}`}
              helperText="The device we should use to preview the line key layout below. You can add this template to any device you want."
              error={errors}
            />
          </Grid>
        </FormGroup>
        <LineKeyEditor
          key={lineKeyTemplate.id}
          lineKeyTemplate={lineKeyTemplate}
          lineKeyTemplateDispatch={dispatch}
          specs={specs}
        />
        <FormGroup
          title="Copy template"
          actions={
            <Button
              onClick={duplicateLineKeyTemplate}
              color="inherit"
              startIcon={<Valkyrie icon={pendingDuplicate ? viSpinnerThird : viCopy} spin={pendingDuplicate} />}
              disabled={pendingDuplicate}
            >
              Duplicate
            </Button>
          }
        />
      </Stack>
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <DialogTitle sx={{ p: 0 }}>Line key template duplicated</DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <DialogContentText>
              You line key template has been duplicated. Would you like to go to the new copy or continue editing the
              current template?
            </DialogContentText>
          </DialogContent>
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              gap: 1,
              containerType: "inline-size",
            }}
          >
            <Button
              component={NavLink}
              to={`../${duplicateId}`}
              onClick={() => setDuplicateDialogOpen(false)}
              color="inherit"
              endIcon={<Valkyrie icon={viArrowRight} />}
            >
              Go to duplicate
            </Button>
            <Button onClick={() => setDuplicateDialogOpen(false)} variant="contained">
              Continue editing
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </Container>
  );
}

export default LineKeyTemplate;
